import { getApi } from './apiService';

/**
 * Rent subscription (Stripe) routes. Separate from leaseService, which covers
 * the lease *agreement* document flow — these are the recurring rent charges a
 * tenant is subscribed to. Kept in one place so the backend contract can be
 * swapped without touching the pages.
 */
export const RENT_ENDPOINTS = {
  subscribe: '/rent/subscribe',
  cancel: '/rent/cancel',
  myLeases: '/rent/my-leases',
  landlordLeases: '/rent/landlord-leases',
  propertyHistory: '/rent/property-history',
};

const RENT_STATUS_LABELS = {
  active: 'Active',
  trialing: 'Trialing',
  past_due: 'Past Due',
  unpaid: 'Unpaid',
  incomplete: 'Payment Pending',
  incomplete_expired: 'Expired',
  canceled: 'Cancelled',
  cancelled: 'Cancelled',
  paused: 'Paused',
  pending: 'Pending',
};

// Green for a healthy subscription, amber while money is still owed, red once
// it has lapsed. Variants reuse the lease-badge palette names.
const RENT_STATUS_VARIANT = {
  active: 'signed',
  trialing: 'sent',
  past_due: 'declined',
  unpaid: 'declined',
  incomplete: 'sent',
  incomplete_expired: 'draft',
  canceled: 'draft',
  cancelled: 'draft',
  paused: 'draft',
  pending: 'sent',
};

// Some environments report the lease state as a number instead of the Stripe
// string: 0:pending, 1:active, 2:cancelled.
const NUMERIC_RENT_STATUS = {
  0: 'pending',
  1: 'active',
  2: 'cancelled',
};

const titleCase = (value) =>
  String(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

export const getRentStatusInfo = (status) => {
  if (status == null || status === '') {
    return { key: '', label: '—', variant: 'draft' };
  }
  const raw = String(status).trim();
  const key = (/^\d+$/.test(raw) ? NUMERIC_RENT_STATUS[Number(raw)] : raw.toLowerCase()) || raw.toLowerCase();
  return {
    key,
    label: RENT_STATUS_LABELS[key] || titleCase(raw),
    variant: RENT_STATUS_VARIANT[key] || 'draft',
  };
};

const firstValue = (data, ...keys) => {
  if (!data || typeof data !== 'object') return null;
  for (const key of keys) {
    const value = data[key];
    if (value != null && value !== '') return value;
  }
  return null;
};

export const formatRentAmount = (value) => {
  if (value == null || value === '') return '—';
  const amount = Number(value);
  if (Number.isNaN(amount)) return String(value);
  return `$${amount.toLocaleString('en-US', { maximumFractionDigits: 2 })}/mo`;
};

/** Stripe sends seconds, our own columns send millis or an ISO string. */
export const formatRentDate = (value) => {
  if (value == null || value === '') return '—';
  const str = String(value);
  let date;
  if (/^\d+$/.test(str)) {
    const num = Number(str);
    // A 10-digit epoch is seconds, anything longer is already milliseconds.
    date = new Date(str.length <= 10 ? num * 1000 : num);
  } else {
    date = new Date(str);
  }
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/**
 * One row of the tenant Leases table. The backend may nest the property or
 * flatten its fields onto the lease, so both shapes are read here.
 */
export const normalizeRentLease = (raw) => {
  if (!raw || typeof raw !== 'object') return null;

  const property = raw.property || raw.Property || raw.property_details || {};
  const landlord = raw.landlord || raw.Landlord || raw.landlord_details || {};

  const address = firstValue(property, 'address') || firstValue(raw, 'property_address') || '';
  const unit = firstValue(property, 'unit_number', 'unitNumber');
  const city = firstValue(property, 'city');
  const state = firstValue(property, 'state');

  const landlordName =
    [firstValue(landlord, 'first_name', 'firstName'), firstValue(landlord, 'last_name', 'lastName')]
      .filter(Boolean)
      .join(' ')
      .trim() ||
    firstValue(landlord, 'name', 'full_name') ||
    firstValue(raw, 'landlord_name') ||
    '';

  const id = firstValue(raw, 'id', 'lease_id', 'leaseId');
  const propertyId = firstValue(raw, 'property_id', 'propertyId') ?? firstValue(property, 'id');

  return {
    id: id != null ? String(id) : null,
    propertyId: propertyId != null ? String(propertyId) : null,
    propertyAddress: unit ? `${address}, ${unit}` : String(address || ''),
    propertyCityState: [city, state].filter(Boolean).join(', '),
    propertyCode: firstValue(property, 'property_code', 'propertyCode') ?? firstValue(raw, 'property_code'),
    landlordName,
    monthlyRent:
      firstValue(raw, 'rent_amount', 'rentAmount', 'monthly_rent', 'monthlyRent', 'rent') ??
      firstValue(property, 'monthly_rent', 'monthlyRent'),
    subscriptionId: firstValue(raw, 'subscription_id', 'subscriptionId', 'stripe_subscription_id'),
    status: firstValue(raw, 'subscription_status', 'subscriptionStatus', 'status', 'lease_status'),
    startDate: firstValue(
      raw,
      'start_date',
      'startDate',
      'lease_start_date',
      'current_period_start',
      'createdAt',
      'created_at'
    ),
    nextPaymentDate: firstValue(
      raw,
      'next_payment_date',
      'nextPaymentDate',
      'next_payment_at',
      'current_period_end',
      'next_billing_date'
    ),
  };
};

/**
 * GET every rent subscription for the signed-in tenant.
 * GET /rent/my-leases -> { data: { leases: [...] } } (also tolerates a plain array)
 */
export const fetchMyRentLeases = async () => {
  const response = await getApi(RENT_ENDPOINTS.myLeases);

  if (response?.statusCode != null && response.statusCode !== 200) {
    if (response.statusCode === 404) return [];
    const error = new Error(response?.message || 'Failed to load leases.');
    error.response = { data: response };
    throw error;
  }

  const payload = response?.data ?? {};
  const rows = Array.isArray(payload)
    ? payload
    : payload.leases ?? payload.data ?? payload.rows ?? [];

  return (Array.isArray(rows) ? rows : []).map(normalizeRentLease).filter(Boolean);
};

const joinName = (...parts) => parts.filter(Boolean).join(' ').trim();

/**
 * One row of the landlord Leases table: the tenant paying rent, the property
 * they pay it on, and the subscription state. The backend may nest the tenant
 * and property or flatten their fields onto the row, so both shapes are read
 * here rather than in the page. Lease-document fields are picked up when the
 * row carries them so the page can link the agreement.
 */
export const normalizeLandlordRentLease = (raw) => {
  if (!raw || typeof raw !== 'object') return null;

  const property = raw.property || raw.Property || raw.property_details || {};
  const tenant = raw.tenant || raw.Tenant || raw.tenant_details || {};

  const address = firstValue(property, 'address') || firstValue(raw, 'property_address') || '';
  const unit = firstValue(property, 'unit_number', 'unitNumber');
  const city = firstValue(property, 'city');
  const state = firstValue(property, 'state');

  const tenantName =
    joinName(firstValue(tenant, 'first_name', 'firstName'), firstValue(tenant, 'last_name', 'lastName')) ||
    firstValue(tenant, 'name', 'full_name') ||
    firstValue(raw, 'tenant_name') ||
    '';

  const id = firstValue(raw, 'id', 'lease_id', 'leaseId');
  const propertyId = firstValue(raw, 'property_id', 'propertyId') ?? firstValue(property, 'id');
  const tenantId = firstValue(raw, 'tenant_id', 'tenantId') ?? firstValue(tenant, 'id');

  return {
    id: id != null ? String(id) : null,
    propertyId: propertyId != null ? String(propertyId) : null,
    tenantId: tenantId != null ? String(tenantId) : null,
    screeningId: firstValue(raw, 'screening_id', 'screeningId'),
    propertyAddress: unit ? `${address}, ${unit}` : String(address || ''),
    propertyCityState: [city, state].filter(Boolean).join(', '),
    propertyCode: firstValue(property, 'property_code', 'propertyCode') ?? firstValue(raw, 'property_code'),
    tenantName,
    tenantEmail: firstValue(tenant, 'email') ?? firstValue(raw, 'tenant_email'),
    monthlyRent:
      firstValue(raw, 'rent_amount', 'rentAmount', 'monthly_rent', 'monthlyRent', 'rent') ??
      firstValue(property, 'monthly_rent', 'monthlyRent'),
    subscriptionId: firstValue(raw, 'subscription_id', 'subscriptionId', 'stripe_subscription_id'),
    status: firstValue(raw, 'subscription_status', 'subscriptionStatus', 'status', 'lease_status'),
    startDate: firstValue(
      raw,
      'start_date',
      'startDate',
      'lease_start_date',
      'current_period_start',
      'createdAt',
      'created_at'
    ),
    nextPaymentDate: firstValue(
      raw,
      'next_payment_date',
      'nextPaymentDate',
      'next_payment_at',
      'current_period_end',
      'next_billing_date'
    ),
    documentTitle: firstValue(raw, 'title', 'lease_title', 'document_title'),
    documentUrl: firstValue(
      raw,
      'signed_document_url',
      'signedDocumentUrl',
      'document',
      'lease_url',
      'document_url',
      'file_url'
    ),
    fileName: firstValue(raw, 'file_name', 'fileName', 'original_name'),
  };
};

/**
 * GET every lease the signed-in landlord collects rent on. Takes no
 * parameters, so the page filters and pages the rows client-side.
 * GET /rent/landlord-leases -> { data: { leases: [...] } } (also tolerates a plain array)
 */
export const fetchLandlordRentLeases = async () => {
  const response = await getApi(RENT_ENDPOINTS.landlordLeases);

  if (response?.statusCode != null && response.statusCode !== 200) {
    if (response.statusCode === 404) return [];
    const error = new Error(response?.message || 'Failed to load leases.');
    error.response = { data: response };
    throw error;
  }

  const payload = response?.data ?? {};
  const rows = Array.isArray(payload)
    ? payload
    : payload.leases ?? payload.data ?? payload.rows ?? [];

  return (Array.isArray(rows) ? rows : []).map(normalizeLandlordRentLease).filter(Boolean);
};

/**
 * The landlord tenant-detail page (/landlord/properties/:id/tenant/:screeningId)
 * is keyed on the tenant's screening, but a lease row only carries the tenant's
 * user id. Match the tenant against the property's applicants to find the
 * screening, preferring the approved application when there are several.
 * GET /property/:id -> { data: { applicants: [{ id | screening_id, tenant_id, tenant, property_tenant_status }] } }
 */
export const resolveLeaseScreeningId = async (lease) => {
  if (!lease?.propertyId) return null;
  if (lease.screeningId != null) return String(lease.screeningId);

  const response = await getApi(`/property/${lease.propertyId}`);
  const applicants = response?.statusCode === 200 ? response?.data?.applicants || [] : [];

  const tenantId = lease.tenantId != null ? String(lease.tenantId) : null;
  const email = lease.tenantEmail ? String(lease.tenantEmail).toLowerCase() : null;
  const matches = applicants.filter((app) => {
    const appTenantId = app.tenant_id ?? app.tenant?.id;
    if (tenantId && appTenantId != null && String(appTenantId) === tenantId) return true;
    return Boolean(email && app.tenant?.email && String(app.tenant.email).toLowerCase() === email);
  });

  const match = matches.find((app) => String(app.property_tenant_status) === '1') || matches[0];
  const screeningId = match ? match.id || match.screening_id : null;
  return screeningId != null ? String(screeningId) : null;
};

// Rent payment states. Stripe reports invoices as paid/open/void/uncollectible
// and charges as succeeded/failed/pending; both vocabularies land here.
const PAYMENT_STATUS_LABELS = {
  paid: 'Paid',
  succeeded: 'Paid',
  open: 'Open',
  pending: 'Pending',
  processing: 'Processing',
  failed: 'Failed',
  uncollectible: 'Uncollectible',
  void: 'Void',
  refunded: 'Refunded',
  draft: 'Draft',
};

const PAYMENT_STATUS_VARIANT = {
  paid: 'signed',
  succeeded: 'signed',
  open: 'sent',
  pending: 'sent',
  processing: 'sent',
  failed: 'declined',
  uncollectible: 'declined',
  void: 'draft',
  refunded: 'draft',
  draft: 'draft',
};

export const getPaymentStatusInfo = (status) => {
  if (status == null || status === '') {
    return { key: '', label: '—', variant: 'draft' };
  }
  const key = String(status).trim().toLowerCase();
  return {
    key,
    label: PAYMENT_STATUS_LABELS[key] || titleCase(status),
    variant: PAYMENT_STATUS_VARIANT[key] || 'draft',
  };
};

/** A plain dollar amount — payments are one-off charges, not `/mo` like rent. */
export const formatPaymentAmount = (value) => {
  if (value == null || value === '') return '—';
  const amount = Number(value);
  if (Number.isNaN(amount)) return String(value);
  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/**
 * One row of a property's rent payment history. Stripe amounts arrive in
 * cents on the `*_amount_*` fields and in dollars on our own columns, so the
 * cents fields are converted here and the row exposes dollars either way.
 */
export const normalizeRentPayment = (raw) => {
  if (!raw || typeof raw !== 'object') return null;

  const tenant = raw.tenant || raw.Tenant || raw.tenant_details || {};

  const dollars = firstValue(raw, 'amount', 'rent_amount', 'rentAmount', 'total');
  const cents = firstValue(raw, 'amount_paid', 'amountPaid', 'amount_due', 'amount_total', 'amount_in_cents');
  const amount = dollars != null ? Number(dollars) : cents != null ? Number(cents) / 100 : null;

  const tenantName =
    joinName(firstValue(tenant, 'first_name', 'firstName'), firstValue(tenant, 'last_name', 'lastName')) ||
    firstValue(tenant, 'name', 'full_name') ||
    firstValue(raw, 'tenant_name') ||
    '';

  const id = firstValue(raw, 'id', 'payment_id', 'paymentId', 'invoice_id', 'invoiceId');

  return {
    id: id != null ? String(id) : null,
    amount: Number.isNaN(amount) ? null : amount,
    currency: (firstValue(raw, 'currency') || 'usd').toString().toUpperCase(),
    status: firstValue(raw, 'status', 'payment_status', 'paymentStatus', 'invoice_status'),
    paidAt: firstValue(
      raw,
      'paid_at',
      'paidAt',
      'payment_date',
      'paymentDate',
      'created',
      'created_at',
      'createdAt'
    ),
    periodStart: firstValue(raw, 'period_start', 'periodStart', 'billing_period_start'),
    periodEnd: firstValue(raw, 'period_end', 'periodEnd', 'billing_period_end'),
    invoiceNumber: firstValue(raw, 'invoice_number', 'invoiceNumber', 'number'),
    receiptUrl: firstValue(
      raw,
      'receipt_url',
      'receiptUrl',
      'invoice_pdf',
      'invoicePdf',
      'hosted_invoice_url',
      'invoice_url'
    ),
    paymentMethod: firstValue(raw, 'payment_method', 'paymentMethod', 'card_brand', 'method'),
    tenantName,
    description: firstValue(raw, 'description', 'note'),
  };
};

/**
 * GET the rent payment history for one property, paginated.
 * GET /rent/property-history?property_id&page&limit
 * -> { data: { payments: [...], currentPage, totalPages, totalItems } }
 * A plain array or a `data`/`rows`/`history` key is tolerated too.
 */
export const fetchPropertyRentHistory = async ({ propertyId, page = 1, limit = 10 }) => {
  if (!propertyId) {
    throw new Error('A property is required to load rent history.');
  }

  const params = new URLSearchParams({
    property_id: String(propertyId),
    page: String(page),
    limit: String(limit),
  });
  const response = await getApi(`${RENT_ENDPOINTS.propertyHistory}?${params.toString()}`);

  const empty = { payments: [], currentPage: page, pageSize: limit, totalPages: 1, totalItems: 0 };

  if (response?.statusCode != null && response.statusCode !== 200) {
    if (response.statusCode === 404) return empty;
    const error = new Error(response?.message || 'Failed to load rent history.');
    error.response = { data: response };
    throw error;
  }

  const payload = response?.data ?? {};
  const rows = Array.isArray(payload)
    ? payload
    : payload.payments ?? payload.history ?? payload.data ?? payload.rows ?? [];
  const list = (Array.isArray(rows) ? rows : []).map(normalizeRentPayment).filter(Boolean);

  return {
    payments: list,
    currentPage: Number(payload.currentPage) || page,
    pageSize: Number(payload.pageSize) || limit,
    totalPages: Number(payload.totalPages) || 1,
    totalItems: Number(payload.totalItems) || list.length,
  };
};
